import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getAlumnos,
  uploadCredencialTemplate,
  getCredencialTemplateInfo,
  generarCredencialesBulk,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  IdentificationCard,
  Upload,
  Printer,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  Sliders,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatNombreCompleto } from '../lib/nombre';

const CredencialesPage = () => {
  const { user, hasPermission } = useAuth();
  const isAdminLevel = hasPermission(['administrador', 'direccion']);

  const [tplInfo, setTplInfo] = useState({ frente: { configurado: false }, dorso: { configurado: false } });
  const [alumnos, setAlumnos] = useState([]);
  const [loadingAlumnos, setLoadingAlumnos] = useState(true);
  const [filters, setFilters] = useState({ grado: 'all', grupo: 'all', turno: 'all', nombre: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [foto, setFoto] = useState({ x: 5, y: 12, w: 25, h: 30 });
  const [incluirDorso, setIncluirDorso] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchTpl = useCallback(async () => {
    try {
      const r = await getCredencialTemplateInfo();
      setTplInfo(r.data);
    } catch (error) {
      // Template info no disponible (probablemente sin permisos para verla); el botón "Generar" detectará la ausencia.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('No se pudo obtener info del template:', error?.message);
      }
    }
  }, []);

  const fetchAlumnos = useCallback(async () => {
    setLoadingAlumnos(true);
    try {
      const params = { limit: 500 };
      if (filters.grado && filters.grado !== 'all') params.grado = filters.grado;
      if (filters.grupo && filters.grupo !== 'all') params.grupo = filters.grupo;
      if (filters.turno && filters.turno !== 'all') params.turno = filters.turno;
      if (filters.nombre) params.nombre = filters.nombre;
      const r = await getAlumnos(params);
      setAlumnos((r.data.alumnos || []).filter((a) => a.estatus === 'activo'));
    } catch (e) {
      toast.error('Error al cargar alumnos');
    } finally {
      setLoadingAlumnos(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTpl();
  }, [fetchTpl]);

  useEffect(() => {
    fetchAlumnos();
  }, [fetchAlumnos]);

  const handleUploadTemplate = async (file, lado) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Máximo 4 MB');
      return;
    }
    try {
      await uploadCredencialTemplate(file, lado);
      toast.success(`Template de ${lado} subido correctamente`);
      fetchTpl();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir template');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === alumnos.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(alumnos.map((a) => a.id)));
  };

  const handleGenerar = async () => {
    if (selectedIds.size === 0) {
      toast.error('Selecciona al menos un alumno');
      return;
    }
    if (!tplInfo.frente?.configurado) {
      toast.error('Primero sube el template del frente en la pestaña "Plantilla"');
      return;
    }
    setGenerating(true);
    try {
      const resp = await generarCredencialesBulk({
        alumno_ids: Array.from(selectedIds),
        incluir_dorso: incluirDorso && tplInfo.dorso?.configurado,
        foto_offset_x_mm: parseFloat(foto.x),
        foto_offset_y_mm: parseFloat(foto.y),
        foto_width_mm: parseFloat(foto.w),
        foto_height_mm: parseFloat(foto.h),
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credenciales_lote_${selectedIds.size}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF con ${selectedIds.size} credenciales descargado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar credenciales');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="credenciales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <IdentificationCard size={28} />
            Credencialización
          </h1>
          <p className="text-muted-foreground">Genera credenciales estudiantiles en serie con tu propio diseño</p>
        </div>
      </div>

      <Tabs defaultValue="generar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="generar" data-testid="tab-generar">Generar en serie</TabsTrigger>
          <TabsTrigger value="plantilla" data-testid="tab-plantilla" disabled={!isAdminLevel}>
            Plantilla del diseño
          </TabsTrigger>
        </TabsList>

        {/* ============ TAB GENERAR ============ */}
        <TabsContent value="generar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Filtrar alumnos</CardTitle>
              <CardDescription>Selecciona uno o varios alumnos para imprimir sus credenciales en una sola hoja</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Nombre / Apellidos</Label>
                  <Input
                    value={filters.nombre}
                    onChange={(e) => setFilters((p) => ({ ...p, nombre: e.target.value }))}
                    placeholder="Buscar..."
                  />
                </div>
                <div>
                  <Label>Grado</Label>
                  <Select value={filters.grado} onValueChange={(v) => setFilters((p) => ({ ...p, grado: v }))}>
                    <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="1">1°</SelectItem>
                      <SelectItem value="2">2°</SelectItem>
                      <SelectItem value="3">3°</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Grupo</Label>
                  <Select value={filters.grupo} onValueChange={(v) => setFilters((p) => ({ ...p, grupo: v }))}>
                    <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="A">A</SelectItem>
                      <SelectItem value="B">B</SelectItem>
                      <SelectItem value="C">C</SelectItem>
                      <SelectItem value="D">D</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Turno</Label>
                  <Select value={filters.turno} onValueChange={(v) => setFilters((p) => ({ ...p, turno: v }))}>
                    <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="matutino">Matutino</SelectItem>
                      <SelectItem value="vespertino">Vespertino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ajustes de foto */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Sliders size={18} /> Ajustes de foto en la credencial</CardTitle>
              <CardDescription>Coordenadas en milímetros desde la esquina superior izquierda de la credencial (85.6mm × 54mm)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Pos X (mm)</Label>
                  <Input type="number" step="0.5" value={foto.x} onChange={(e) => setFoto((p) => ({ ...p, x: e.target.value }))} data-testid="foto-x" />
                </div>
                <div>
                  <Label>Pos Y (mm)</Label>
                  <Input type="number" step="0.5" value={foto.y} onChange={(e) => setFoto((p) => ({ ...p, y: e.target.value }))} data-testid="foto-y" />
                </div>
                <div>
                  <Label>Ancho (mm)</Label>
                  <Input type="number" step="0.5" value={foto.w} onChange={(e) => setFoto((p) => ({ ...p, w: e.target.value }))} data-testid="foto-w" />
                </div>
                <div>
                  <Label>Alto (mm)</Label>
                  <Input type="number" step="0.5" value={foto.h} onChange={(e) => setFoto((p) => ({ ...p, h: e.target.value }))} data-testid="foto-h" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="incluir-dorso"
                  checked={incluirDorso}
                  onChange={(e) => setIncluirDorso(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                  data-testid="incluir-dorso"
                />
                <Label htmlFor="incluir-dorso" className="cursor-pointer">Incluir dorso (si está configurado)</Label>
              </div>
            </CardContent>
          </Card>

          {/* Tabla de alumnos */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Selección de alumnos</CardTitle>
                <CardDescription>{selectedIds.size} seleccionado(s) de {alumnos.length}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={toggleSelectAll} data-testid="toggle-all">
                  {selectedIds.size === alumnos.length && alumnos.length ? 'Quitar todos' : 'Seleccionar todos'}
                </Button>
                <Button
                  onClick={handleGenerar}
                  disabled={generating || selectedIds.size === 0 || !tplInfo.frente?.configurado}
                  data-testid="generar-bulk-btn"
                >
                  <Printer size={16} className="mr-2" />
                  {generating ? 'Generando...' : `Imprimir ${selectedIds.size || ''} credencial(es)`}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[420px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[60px]"></TableHead>
                      <TableHead className="w-[110px]">Matrícula</TableHead>
                      <TableHead>Nombre completo</TableHead>
                      <TableHead className="w-[80px]">Grado</TableHead>
                      <TableHead className="w-[80px]">Grupo</TableHead>
                      <TableHead className="w-[110px]">Turno</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingAlumnos ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8"><div className="spinner mx-auto" /></TableCell></TableRow>
                    ) : alumnos.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay alumnos con esos filtros</TableCell></TableRow>
                    ) : (
                      alumnos.map((a) => (
                        <TableRow key={a.id} className="hover:bg-muted/30">
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(a.id)}
                              onChange={() => toggleSelect(a.id)}
                              className="h-4 w-4 accent-primary"
                              data-testid={`select-${a.matricula}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{a.matricula}</TableCell>
                          <TableCell className="font-medium min-w-[160px] max-w-[260px]">
                            <span
                              className="text-xs sm:text-sm md:text-base break-words whitespace-normal leading-tight block"
                              title={formatNombreCompleto(a)}
                            >
                              {formatNombreCompleto(a)}
                            </span>
                          </TableCell>
                          <TableCell>{a.grado}°</TableCell>
                          <TableCell>{a.grupo}</TableCell>
                          <TableCell className="capitalize">{a.turno}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ TAB PLANTILLA ============ */}
        <TabsContent value="plantilla" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Diseño de la credencial</CardTitle>
              <CardDescription>
                Sube las imágenes de fondo del frente y dorso de la credencial (tamaño recomendado: 85.6mm × 54mm @ 300 DPI ≈ 1011 × 638 px).
                Sobre ellas se imprimirán la foto y los datos del alumno.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">Frente</h3>
                    {tplInfo.frente?.configurado ? (
                      <Badge className="bg-green-100 text-green-800"><CheckCircle size={12} className="mr-1" />Configurado</Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-700"><XCircle size={12} className="mr-1" />Falta</Badge>
                    )}
                  </div>
                  <label className="block">
                    <Button asChild variant="outline">
                      <span className="cursor-pointer">
                        <Upload size={16} className="mr-2" />
                        {tplInfo.frente?.configurado ? 'Reemplazar imagen del frente' : 'Subir imagen del frente'}
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleUploadTemplate(e.target.files[0], 'frente')}
                      data-testid="upload-frente"
                    />
                  </label>
                  <p className="text-xs text-muted-foreground">PNG / JPG / WEBP — máximo 4 MB</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">Dorso (opcional)</h3>
                    {tplInfo.dorso?.configurado ? (
                      <Badge className="bg-green-100 text-green-800"><CheckCircle size={12} className="mr-1" />Configurado</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground"><ImageIcon size={12} className="mr-1" />Sin subir</Badge>
                    )}
                  </div>
                  <label className="block">
                    <Button asChild variant="outline">
                      <span className="cursor-pointer">
                        <Upload size={16} className="mr-2" />
                        {tplInfo.dorso?.configurado ? 'Reemplazar imagen del dorso' : 'Subir imagen del dorso'}
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleUploadTemplate(e.target.files[0], 'dorso')}
                      data-testid="upload-dorso"
                    />
                  </label>
                  <p className="text-xs text-muted-foreground">Si lo subes, se imprimirá una segunda hoja con los dorsos en la misma cuadrícula.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CredencialesPage;
